import { useState } from "react";
import { Send, MapPin, Mail, Phone, Instagram, Youtube, MessageCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { IMG } from "../lib/images";
import { useSettings } from "../lib/settings";
import { TikTokIcon } from "../components/SocialIcons";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/motion";

export default function Contact() {
  const { settings } = useSettings();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", subject: "", message: "", website: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHeader
        testid="contact-header"
        eyebrow="Contact"
        titleLines={["Écrivez-nous,", "nous vous répondrons"]}
        intro="Une question, une demande, une envie de découvrir l'église ? Nous serions heureux d'échanger avec vous."
        image={IMG.contact}
      />
      <section className="py-16 sm:py-24">
        <div className="container-site grid items-start gap-12 lg:grid-cols-[1fr_1.1fr]">
          <div className="space-y-6">
            <Reveal>
              <div className="card-warm p-7 sm:p-8">
                <p className="font-serif text-xl font-semibold text-obsidian">La Maison de la Charité</p>
                <p className="mt-2 flex items-start gap-2.5 text-sm leading-relaxed text-stone-600">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-terracotta" />
                  {settings.city_line}
                </p>
                {settings.contact_email && (
                  <a data-testid="contact-email-link" href={`mailto:${settings.contact_email}`}
                    className="mt-3 flex items-center gap-2.5 text-sm font-semibold text-terracotta hover:underline">
                    <Mail className="h-4 w-4" /> {settings.contact_email}
                  </a>
                )}
                {settings.phone && (
                  <a data-testid="contact-phone-link" href={`tel:${settings.phone.replace(/\s/g, "")}`}
                    className="mt-3 flex items-center gap-2.5 text-sm font-semibold text-terracotta hover:underline">
                    <Phone className="h-4 w-4" /> {settings.phone}
                  </a>
                )}
                <div className="mt-5 flex gap-3">
                  {settings.instagram && (
                    <a data-testid="contact-instagram" href={settings.instagram} target="_blank" rel="noreferrer" aria-label="Instagram"
                      className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-line bg-white text-stone-600 transition-colors hover:border-terracotta hover:text-terracotta">
                      <Instagram className="h-4 w-4" />
                    </a>
                  )}
                  {settings.youtube && (
                    <a data-testid="contact-youtube" href={settings.youtube} target="_blank" rel="noreferrer" aria-label="YouTube"
                      className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-line bg-white text-stone-600 transition-colors hover:border-terracotta hover:text-terracotta">
                      <Youtube className="h-4 w-4" />
                    </a>
                  )}
                  {settings.tiktok && (
                    <a data-testid="contact-tiktok" href={settings.tiktok} target="_blank" rel="noreferrer" aria-label="TikTok"
                      className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-line bg-white text-stone-600 transition-colors hover:border-terracotta hover:text-terracotta">
                      <TikTokIcon className="h-4 w-4" />
                    </a>
                  )}
                  {settings.whatsapp && (
                    <a data-testid="contact-whatsapp" href={settings.whatsapp} target="_blank" rel="noreferrer" aria-label="WhatsApp"
                      className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-line bg-white text-stone-600 transition-colors hover:border-terracotta hover:text-terracotta">
                      <MessageCircle className="h-4 w-4" />
                    </a>
                  )}
                </div>
                <a data-testid="contact-itinerary-btn" href={settings.maps_url} target="_blank" rel="noreferrer" className="btn-outline mt-6">
                  <MapPin className="h-4 w-4" /> Itinéraire
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="overflow-hidden rounded-[2rem] border border-line">
                <iframe
                  data-testid="contact-map"
                  title="Carte — La Maison de la Charité, Montévrain / Val d'Europe"
                  src="https://www.google.com/maps?q=R%C3%A9sidence%20du%20Parc%2C%202%20Rue%20Edouard%20Buffard%2C%2077144%20Mont%C3%A9vrain&z=15&output=embed"
                  className="map-frame h-64 w-full border-0 sm:h-80"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            {sent ? (
              <div data-testid="contact-form-success" className="card-warm p-10 text-center">
                <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-olive-soft text-olive-deep">
                  <CheckCircle2 className="h-6 w-6" />
                </span>
                <p className="mt-6 font-serif text-2xl font-medium text-obsidian">Message bien reçu</p>
                <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-stone-600">
                  Merci pour votre message. Nous vous répondrons avec joie dans les meilleurs délais.
                </p>
                <button data-testid="contact-form-again" onClick={() => { setSent(false); setForm({ first_name: "", last_name: "", email: "", phone: "", subject: "", message: "", website: "" }); }}
                  className="btn-outline mt-8">
                  Écrire un autre message
                </button>
              </div>
            ) : (
              <form data-testid="contact-form" onSubmit={submit} className="card-warm space-y-4 p-7 sm:p-9">
                <input type="text" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="c-firstname" className="mb-1.5 block text-sm font-semibold text-obsidian">Prénom *</label>
                    <input id="c-firstname" data-testid="contact-firstname-input" required className="input-field" value={form.first_name}
                      onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
                  </div>
                  <div>
                    <label htmlFor="c-lastname" className="mb-1.5 block text-sm font-semibold text-obsidian">Nom</label>
                    <input id="c-lastname" data-testid="contact-lastname-input" className="input-field" value={form.last_name}
                      onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="c-email" className="mb-1.5 block text-sm font-semibold text-obsidian">Email *</label>
                    <input id="c-email" data-testid="contact-email-input" type="email" required className="input-field" value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  </div>
                  <div>
                    <label htmlFor="c-phone" className="mb-1.5 block text-sm font-semibold text-obsidian">Téléphone (facultatif)</label>
                    <input id="c-phone" data-testid="contact-phone-input" type="tel" className="input-field" value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                  </div>
                </div>
                <div>
                  <label htmlFor="c-subject" className="mb-1.5 block text-sm font-semibold text-obsidian">Objet *</label>
                  <input id="c-subject" data-testid="contact-subject-input" required className="input-field" value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Ex. Je souhaite découvrir l'église" />
                </div>
                <div>
                  <label htmlFor="c-message" className="mb-1.5 block text-sm font-semibold text-obsidian">Message *</label>
                  <textarea id="c-message" data-testid="contact-message-input" required rows={5} className="input-field resize-none" value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })} />
                </div>
                <button data-testid="contact-submit-btn" type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-60">
                  <Send className="h-4 w-4" />
                  {loading ? "Envoi…" : "Envoyer le message"}
                </button>
                <p className="text-center text-xs text-stone-400">
                  Vos données sont utilisées uniquement pour vous répondre — voir notre politique de confidentialité.
                </p>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </>
  );
}
